import React from 'react';
import { Field } from '@base-ui/react/field';
import { Tabs } from '@base-ui/react/tabs';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';
import { Button } from '@ui/Button/beta';
import { Modal } from '@ui/Modal';
import ErrorMessage from '@ui/ErrorMessage';
import DropZone from '../../ImportScript/Dropzone';
import styles from './ImportModal.module.css';

export type ImportModalMethod = 'import' | 'paste' | 'url';

const IMPORT_MODAL_METHODS: ImportModalMethod[] = ['import', 'paste', 'url'];

const isImportModalMethod = (value: unknown): value is ImportModalMethod =>
  IMPORT_MODAL_METHODS.includes(value as ImportModalMethod);

export type ImportModalSubmission =
  | { method: 'import'; file: File }
  | { method: 'paste'; text: string }
  | { method: 'url'; url: string };

const DEFAULT_FILE_TYPES = ['.docx', '.txt', '.pdf', '.ppt', '.csv'];

export type ImportModalProps = {
  id?: string;
  'data-e2e'?: string;
  open?: boolean;
  ctaText: string;
  close: () => void;
  defaultMethod?: ImportModalMethod;
  /** Seeds the "already has content" states — used by Storybook to show the filled panels. */
  defaultFile?: File | null;
  defaultText?: string;
  defaultUrl?: string;
  acceptedFileTypes?: string[];
  fileAccept?: string;
  learnMoreHref?: string;
  onSubmit?: (submission: ImportModalSubmission) => void;
  /** Shown when setup failed and the import modal is reopened. */
  errorReason?: string;
};

/**
 * "Add your script" — import a file, paste text, or link a URL, then create
 * a project from it. Built from the Figma "Guided project creation" import
 * step; composes `Modal` + Base UI `Tabs`/`Field`.
 */
function ImportModal(props: ImportModalProps) {
  const {
    id = 'import-modal',
    'data-e2e': dataE2E,
    open,
    close,
    defaultMethod = 'import',
    defaultFile = null,
    defaultText = '',
    defaultUrl = '',
    acceptedFileTypes = DEFAULT_FILE_TYPES,
    fileAccept,
    learnMoreHref = 'https://help.wellsaid.io/hc/en-us/articles/44882415828883-Upload-a-Script-to-the-Studio-Editor',
    onSubmit,
    errorReason,
    ctaText,
  } = props;

  const [method, setMethod] = React.useState<ImportModalMethod>(defaultMethod);
  const [file, setFile] = React.useState<File | null>(defaultFile);
  const [text, setText] = React.useState(defaultText);
  const [url, setUrl] = React.useState(defaultUrl);

  const canCreate =
    (method === 'import' && file !== null) ||
    (method === 'paste' && text.trim().length > 0) ||
    (method === 'url' && url.trim().length > 0);

  const handleMethodChange = (value: unknown) => {
    if (isImportModalMethod(value)) setMethod(value);
  };

  const handleFileChange = (
    fileInstance: File | null,
    errorType?: 'FILE_TYPE' | 'SIZE_LIMIT'
  ) => {
    if (!errorType) {
      setFile(fileInstance);
    }
  };

  const handleSubmit = () => {
    if (method === 'import' && file) {
      onSubmit?.({ method, file });
    }
    if (method === 'paste' && text.trim()) {
      onSubmit?.({ method, text });
    }
    if (method === 'url' && url.trim()) {
      onSubmit?.({ method, url });
    }
  };

  return (
    <Modal
      id={id}
      data-e2e={dataE2E ?? id}
      open={open}
      close={close}
      size="md"
      title={
        <div className={styles.heading}>
          <Typography variant="h3" component="p">
            Add your script
          </Typography>
          <Typography variant="pMd" component="p" color="secondary">
            We&rsquo;ll structure it and assign voices, you can edit and make
            changes after setup.
          </Typography>
        </div>
      }
    >
      <Tabs.Root
        value={method}
        onValueChange={handleMethodChange}
        className={styles.main}
      >
        <div className={styles['import-space']}>
          <Tabs.List className={styles['toggle-group']}>
            <Tabs.Tab value="import" className={styles['toggle-tab']}>
              <Typography variant="h5" component="span" color="inherit">
                Import
              </Typography>
            </Tabs.Tab>
            <Tabs.Tab value="paste" className={styles['toggle-tab']}>
              <Typography variant="h5" component="span" color="inherit">
                Paste text
              </Typography>
            </Tabs.Tab>
            <Tabs.Tab value="url" className={styles['toggle-tab']}>
              <Typography variant="h5" component="span" color="inherit">
                URL
              </Typography>
            </Tabs.Tab>
          </Tabs.List>

          <div className={styles['panel-viewport']}>
            <Tabs.Panel keepMounted value="import" className={styles.panel}>
              <div className={styles['panel-inner']}>
                <DropZone
                  accept={fileAccept || acceptedFileTypes.join(',')}
                  onChange={handleFileChange}
                  sizeLimit="10MB"
                />
                {errorReason ? (
                  <div style={{ paddingTop: 12 }}>
                    <ErrorMessage type="error">{errorReason}</ErrorMessage>
                  </div>
                ) : null}
              </div>
            </Tabs.Panel>

            <Tabs.Panel keepMounted value="paste" className={styles.panel}>
              <div className={styles['panel-inner']}>
                <Field.Root className={styles['paste-field']}>
                  <Field.Control
                    render={<textarea aria-label="Script text" />}
                    placeholder="Paste your script here"
                    value={text}
                    onChange={event => setText(event.target.value)}
                    className={styles.textarea}
                  />
                  <Typography
                    variant="pXs"
                    component="span"
                    color="secondary"
                    className={styles['char-count']}
                  >
                    {text.length} characters
                  </Typography>
                </Field.Root>
                {errorReason ? (
                  <div style={{ paddingTop: 12 }}>
                    <ErrorMessage type="error">{errorReason}</ErrorMessage>
                  </div>
                ) : null}
              </div>
            </Tabs.Panel>

            <Tabs.Panel keepMounted value="url" className={styles.panel}>
              <div className={styles['panel-inner']}>
                <Field.Root className={styles['url-field']}>
                  <Field.Label className={styles['url-label']}>
                    <Typography variant="pSm" component="span" bold>
                      URL
                    </Typography>
                  </Field.Label>
                  <Field.Control
                    type="url"
                    placeholder="Paste URL"
                    value={url}
                    onChange={event => setUrl(event.target.value)}
                    className={styles['url-input']}
                  />
                </Field.Root>
                {errorReason ? (
                  <div style={{ paddingTop: 12 }}>
                    <ErrorMessage type="error">{errorReason}</ErrorMessage>
                  </div>
                ) : null}
              </div>
            </Tabs.Panel>
          </div>
        </div>
      </Tabs.Root>

      <div className={styles.footer}>
        <Button
          variant="text"
          className={styles['learn-more']}
          /* eslint-disable jsx-a11y/anchor-has-content, jsx-a11y/control-has-associated-label -- Base UI's `render` prop merges Button's own children into this element at runtime; the `<a>` isn't actually empty. Prettier may reflow this prop onto its own lines, so this is a range disable rather than `-next-line`. */
          render={<a href={learnMoreHref} target="_blank" rel="noreferrer" />}
          /* eslint-enable jsx-a11y/anchor-has-content, jsx-a11y/control-has-associated-label */
        >
          <Typography variant="pMd" component="span">
            Learn more
          </Typography>
          <Icon type="arrowUpRight" htmlColor="currentColor" />
        </Button>

        <Button
          variant="primary"
          size="large"
          className={styles['create-button']}
          disabled={!canCreate}
          onClick={handleSubmit}
        >
          <Icon type="stars03" fontSize="medium" htmlColor="currentColor" />
          <Typography variant="h4" component="span" bold color="inherit">
            {ctaText}
          </Typography>
        </Button>
      </div>
    </Modal>
  );
}

export default ImportModal;
